/**
 * Field validation functions
 * Migrated from Alpine.js field-config.js FieldValidator
 */

import { ValidationRule, ValidationResult, ValidationRuleType, FieldSchema } from '@/config/fieldConfig';

/**
 * Validate a field value against its schema
 */
export function validateField(
  fieldName: string,
  value: any,
  fieldConfig: FieldSchema,
  appContext?: any
): ValidationResult {
  if (!fieldConfig) {
    console.error(`Field ${fieldName} not found in FIELD_CONFIG`);
    return {
      valid: false,
      isWarning: false,
      message: `Error: Field ${fieldName} has no configuration defined`,
    };
  }

  if (!fieldConfig.validation || fieldConfig.validation.length === 0) {
    return { valid: true };
  }

  for (const rule of fieldConfig.validation) {
    const result = validateRule(rule, value, appContext);
    if (!result.valid) {
      return result;
    }
  }

  return { valid: true };
}

/**
 * Validate a single validation rule
 */
export function validateRule(
  rule: ValidationRule,
  value: any,
  appContext?: any
): ValidationResult {
  switch (rule.rule) {
    case ValidationRuleType.REQUIRED:
      if (!value || (typeof value === 'string' && !value.trim())) {
        return {
          valid: false,
          message: rule.message || 'This field is required',
        };
      }
      break;

    case ValidationRuleType.MIN:
      if (typeof value === 'number' && value < rule.value!) {
        return {
          valid: false,
          message: rule.message || `Value must be at least ${rule.value}`,
        };
      }
      break;

    case ValidationRuleType.MAX:
      if (typeof value === 'number' && value > rule.value!) {
        return {
          valid: false,
          message: rule.message || `Value must be at most ${rule.value}`,
        };
      }
      break;

    case ValidationRuleType.MIN_LENGTH:
      if (typeof value === 'string' && value.length < rule.value!) {
        return {
          valid: false,
          message: rule.message || `Must be at least ${rule.value} characters`,
        };
      }
      break;

    case ValidationRuleType.MAX_LENGTH:
      if (typeof value === 'string' && value.length > rule.value!) {
        return {
          valid: false,
          message: rule.message || `Must be at most ${rule.value} characters`,
        };
      }
      break;

    case ValidationRuleType.PATTERN:
      if (rule.pattern && typeof value === 'string' && !rule.pattern.test(value)) {
        return {
          valid: false,
          message: rule.message || 'Invalid format',
        };
      }
      break;

    case ValidationRuleType.IN_OPTIONS:
      if (value) {
        let validOptions: string[] = [];

        // Get options from static list or dynamic getter
        if (rule.options) {
          validOptions = rule.options;
        } else if (rule.getOptions && appContext) {
          validOptions = rule.getOptions(appContext);
        }

        if (validOptions.length > 0 && !validOptions.includes(value)) {
          return {
            valid: false,
            isWarning: true, // This is a warning, not an error
            message: rule.message || `Value '${value}' is not in the list of valid options`,
          };
        }
      }
      break;

    case ValidationRuleType.CUSTOM:
      if (rule.validate) {
        const result = rule.validate(value, appContext);
        if (!result.valid) {
          return result;
        }
      }
      break;
  }

  return { valid: true };
}

/**
 * Validate all fields in a configuration object
 */
export function validateConfig(
  config: Record<string, any>,
  fieldConfigs: Record<string, FieldSchema>,
  appContext?: any
): Record<string, ValidationResult> {
  const errors: Record<string, ValidationResult> = {};

  for (const [fieldName, fieldConfig] of Object.entries(fieldConfigs)) {
    // Skip fields that are conditionally hidden
    if (fieldConfig.showIf && !fieldConfig.showIf(config)) {
      continue;
    }

    const value = config[fieldName];
    const result = validateField(fieldName, value, fieldConfig, appContext);

    if (!result.valid) {
      errors[fieldName] = result;
    }
  }

  return errors;
}

/**
 * Check if there are any validation errors (excluding warnings)
 */
export function hasErrors(validationResults: Record<string, ValidationResult>): boolean {
  return Object.values(validationResults).some((result) => !result.valid && !result.isWarning);
}

/**
 * Check if there are any validation warnings
 */
export function hasWarnings(validationResults: Record<string, ValidationResult>): boolean {
  return Object.values(validationResults).some((result) => !result.valid && result.isWarning);
}

/**
 * Get only errors (excluding warnings)
 */
export function getErrors(validationResults: Record<string, ValidationResult>): Record<string, ValidationResult> {
  const errors: Record<string, ValidationResult> = {};
  for (const [key, result] of Object.entries(validationResults)) {
    if (!result.valid && !result.isWarning) {
      errors[key] = result;
    }
  }
  return errors;
}

/**
 * Get only warnings
 */
export function getWarnings(validationResults: Record<string, ValidationResult>): Record<string, ValidationResult> {
  const warnings: Record<string, ValidationResult> = {};
  for (const [key, result] of Object.entries(validationResults)) {
    if (!result.valid && result.isWarning) {
      warnings[key] = result;
    }
  }
  return warnings;
}
